import { UploadIcon } from "@/Icons/v2/Library/UploadIcon";
import { allAllowedFileTypes } from "@/Utils/fileValidation";
import { Translate, useTrans } from "@/translations";

import { useCallback, useRef, useState } from "react";

import { twMerge } from "tailwind-merge";

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

type Props = {
  hasFileError: boolean;
  setHasFileError: (value: boolean) => void;
  file?: File | null;
  setFile: (value: File | null) => void;
};

export const UploadZone: React.FC<Props> = ({
  hasFileError,
  setHasFileError,
  file,
  setFile,
}) => {
  const { t } = useTrans();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const onUploadZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragActive(false);
  }, []);

  const uploadFile = useCallback(
    (file: File | undefined | null) => {
      if (!file) return;
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setHasFileError(true);
        return;
      }
      setHasFileError(false);
      setFile(file);
    },
    [setFile, setHasFileError],
  );

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      uploadFile(e.target.files?.[0]);
      e.target.value = "";
    },
    [uploadFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragActive(false);
      uploadFile(e.dataTransfer.files?.[0]);
    },
    [uploadFile],
  );

  const removeFile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setFile(null);
      setHasFileError(false);
    },
    [setFile, setHasFileError],
  );

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        onChange={onFileInputChange}
        className="hidden"
        accept={allAllowedFileTypes.join(", ")}
      />
      <div
        onClick={onUploadZoneClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={twMerge(
          "cursor-pointer rounded-lg border-[1.5px] border-dashed bg-[#FBF8F8] p-[26px] text-center",
          isDragActive ? "border-black bg-[#F3F5AE]" : "border-grey3",
        )}
      >
        {file ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-[13.5px] font-semibold text-[#1D1C1C]">
              {file.name}
            </span>
            <span className="text-[11.5px] text-[#9a958f]">
              ({formatFileSize(file.size)})
            </span>
            <button
              type="button"
              onClick={removeFile}
              className="cursor-pointer text-[11.5px] font-medium text-[#B73A23] underline"
            >
              {t("libraryPage:fileUploadRemoveFile")}
            </button>
          </div>
        ) : (
          <>
            <UploadIcon />
            <div className="mt-2 text-[13.5px] text-[#4a4747]">
              <Translate i18nKey="libraryPage:fileUploadDropText">
                <span className="font-semibold underline" />
              </Translate>
            </div>
            <div className="mt-1 text-[11.5px] text-[#9a958f]">
              {t("libraryPage:fileUploadFileLimits")}
            </div>
          </>
        )}
      </div>
      {hasFileError && (
        <div className="text-[12px] text-[#B73A23]">
          {t("libraryPage:fileTooLargeError")}
        </div>
      )}
    </div>
  );
};
